import { AppRoutes } from '@/shared/lib/routes';
import { createRoute } from '@tanstack/react-router';
import { useEffect, useMemo, useState } from 'react';
import { rootRoute } from './__root';
import { usePlans } from '@/features/products';
import type { ProductPlan } from '@/features/products';
import { useUpsell } from '@/features/payments';
import { ANALYTICS_EVENTS, trackEvent } from '@/features/analytics';
import { usePurchaseStore } from '@/stores/purchase-store';
import { useNavigateWithParams } from '@/shared/hooks/use-navigate-with-params';
import { LoadingSpinner } from '@/shared/components/loading-spinner';
import {
  resolveFunnelSteps,
  funnelOfferLabels,
  useOffersFunnelStore,
  PriorityOfferPage,
  CoverageOfferPage,
  RegisterStep,
  GetAppStep,
} from '@/features/offers';
import type { CoverageCategoryKey } from '@/features/offers';

export const additionalOffersRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: AppRoutes.ADDITIONAL_OFFERS,
  component: AdditionalOffersPage,
});

function usePurchaseHydrated(): boolean {
  const [hydrated, setHydrated] = useState(
    () => usePurchaseStore.persist.hasHydrated() && useOffersFunnelStore.persist.hasHydrated(),
  );
  useEffect(() => {
    const check = () => {
      if (usePurchaseStore.persist.hasHydrated() && useOffersFunnelStore.persist.hasHydrated()) {
        setHydrated(true);
      }
    };
    const unsubPurchase = usePurchaseStore.persist.onFinishHydration(check);
    const unsubFunnel = useOffersFunnelStore.persist.onFinishHydration(check);
    check();
    return () => {
      unsubPurchase();
      unsubFunnel();
    };
  }, []);
  return hydrated;
}

function AdditionalOffersPage() {
  const navigate = useNavigateWithParams();
  const hydrated = usePurchaseHydrated();
  const step = useOffersFunnelStore((s) => s.step);
  const setStep = useOffersFunnelStore((s) => s.setStep);
  const purchaseStatus = usePurchaseStore((s) => s.status);
  const { purchase, status: upsellStatus, error: upsellError, canPurchase } = useUpsell();

  const steps = useMemo(() => resolveFunnelSteps(), []);
  const offerLabels = useMemo(() => funnelOfferLabels(steps), [steps]);
  const current = steps[Math.min(step, Math.max(steps.length - 1, 0))];

  const coveragePlanIds = current?.kind === 'coverage' ? current.planIds : {};
  const planIds = useMemo(() => {
    if (current?.kind === 'priority') return [current.planId];
    if (current?.kind === 'coverage') {
      return Object.values(current.planIds).filter(Boolean) as string[];
    }
    return [];
  }, [current]);
  const { plans, status: plansStatus, isBackendConfigured } = usePlans(planIds);

  useEffect(() => {
    if (!hydrated) return;
    if (purchaseStatus !== 'success') {
      navigate({ to: AppRoutes.CHECKOUT });
    }
  }, [hydrated, purchaseStatus, navigate]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [step]);

  useEffect(() => {
    if (steps.length === 0) return;
    if (step > steps.length - 1) setStep(steps.length - 1);
  }, [step, steps.length, setStep]);

  const waitingForPlans =
    (current?.kind === 'priority' || current?.kind === 'coverage') &&
    planIds.length > 0 &&
    plans.length === 0 &&
    isBackendConfigured &&
    plansStatus !== 'error';

  if (!hydrated || purchaseStatus !== 'success' || !current || waitingForPlans) {
    return (
      <div className="flex min-h-[100dvh] items-center justify-center bg-mf-neutral-50">
        <LoadingSpinner />
      </div>
    );
  }

  const goNext = () => {
    if (step + 1 >= steps.length) return;
    setStep(step + 1);
  };

  const purchasing = upsellStatus === 'processing';

  if (current.kind === 'priority') {
    const plan = plans.find((p) => p.id === current.planId);
    const handleUnlock = async () => {
      trackEvent(ANALYTICS_EVENTS.SETTLEMENT_PRIORITY_BUTTON_CLICK, {
        productId: plan?.id,
        price: plan?.price,
        currency: plan?.currency,
      });
      if (!plan) {
        if (plansStatus === 'loading' || plansStatus === 'idle') return;
        goNext();
        return;
      }
      if (!canPurchase) {
        goNext();
        return;
      }
      const ok = await purchase(plan, { offer: 'priority' });
      if (ok) goNext();
    };
    return (
      <PriorityOfferPage
        offerLabels={offerLabels}
        currentIndex={step}
        plan={plan}
        purchasing={purchasing}
        error={upsellError}
        onUnlock={() => void handleUnlock()}
        onSkip={() => {
          trackEvent(ANALYTICS_EVENTS.SETTLEMENT_PRIORITY_SKIP);
          goNext();
        }}
      />
    );
  }

  if (current.kind === 'coverage') {
    const plansByKey: Partial<Record<CoverageCategoryKey, ProductPlan>> = {};
    for (const key of Object.keys(coveragePlanIds) as CoverageCategoryKey[]) {
      const id = coveragePlanIds[key];
      const match = id ? plans.find((p) => p.id === id) : undefined;
      if (match) plansByKey[key] = match;
    }
    const available = Object.keys(plansByKey) as CoverageCategoryKey[];
    const handleUnlock = async (keys: CoverageCategoryKey[]) => {
      trackEvent(ANALYTICS_EVENTS.SETTLEMENT_COVERAGE_BUTTON_CLICK, {
        metadata: { categories: keys.join(',') },
      });
      for (const key of keys) {
        const plan = plansByKey[key];
        if (plan && canPurchase) await purchase(plan, { offer: 'coverage', category: key });
      }
      goNext();
    };
    return (
      <CoverageOfferPage
        offerLabels={offerLabels}
        currentIndex={step}
        available={available}
        plansByKey={plansByKey}
        purchasing={purchasing}
        onUnlock={(keys) => void handleUnlock(keys)}
        onSkip={() => {
          trackEvent(ANALYTICS_EVENTS.SETTLEMENT_COVERAGE_SKIP);
          goNext();
        }}
      />
    );
  }

  if (current.kind === 'register') {
    return <RegisterStep offerLabels={offerLabels} currentIndex={step} onComplete={goNext} />;
  }

  return <GetAppStep offerLabels={offerLabels} currentIndex={step} />;
}
